import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGo, useNextStep } from '../../app/navigation';
import { Screen } from '../../chrome';
import { Button, FileTrigger, Hint, Note, Question, ScanZone, Sub, TextLink } from '../../components';
import { useT } from '../../i18n';
import { useTrack } from '../../lib/analytics';
import {
  ApiError,
  isTurnstileError,
  queryKeys,
  useApi,
  useCreateEnquiryFromScan,
  useDocument,
  type Enquiry,
} from '../../lib/api';
import { currentEnquiry, startNewCheck, tripDraft, updateTripDraft } from '../../lib/funnel';
import { TrustPills } from '../S3/TrustPills';

/** ux-flow.md S0b: ~10 s before we say we're still working. */
const STILL_WORKING_MS = 10_000;

/**
 * Why the scan didn't work, each worded apart (w4-01 fixes 9, 17): nothing on it to read, the
 * reader or the API is down (not the visitor's photo or connection), an empty file, too big,
 * a type we can't read, the visitor's connection, or Turnstile refusing us twice (w5-11).
 */
type Failure = 'unreadable' | 'unavailable' | 'empty' | 'too_large' | 'unsupported' | 'network' | 'robot';

/** An upload or a poll that failed: the API answered 5xx (or the edge did) → our side; no answer → theirs. */
function failureOf(e: unknown): Failure {
  if (!(e instanceof ApiError)) return 'network';
  if (e.code === 'file_empty') return 'empty';
  if (e.code === 'file_too_large') return 'too_large';
  if (e.code === 'unsupported_file_type') return 'unsupported';
  if (isTurnstileError(e)) return 'robot';
  return e.status >= 500 ? 'unavailable' : 'network';
}

/**
 * S0b · Scan your boarding pass. Upload → "Reading your boarding pass…" → full read: the
 * resolver (S5); partial: S1 with what was read and a notice; nothing, or the reader
 * unavailable: the failure state, worded for each (Try again / Enter flight details).
 * The scan is a new check: only the contact carries over.
 */
export default function ScreenS0b() {
  const t = useT();
  const go = useGo();
  const track = useTrack();
  const api = useApi();
  const qc = useQueryClient();
  const { goNext } = useNextStep();
  const scan = useCreateEnquiryFromScan();
  const [pending, setPending] = useState<{ enquiryId: string; documentId: string } | null>(null);
  // Upload refused (size, type, network) or the read failed after upload.
  const [uploadFailure, setUploadFailure] = useState<Failure | null>(null);
  const [slow, setSlow] = useState(false);
  const doc = useDocument(pending?.enquiryId, pending?.documentId);
  const d = doc.data;
  const outcome =
    d && d.status !== 'processing'
      ? d.read?.outcome === 'unavailable'
        ? 'unavailable'
        : d.status === 'rejected'
          ? 'none'
          : (d.read?.outcome ?? 'none')
      : null;
  const failure: Failure | null =
    uploadFailure ??
    (outcome === 'none'
      ? 'unreadable'
      : outcome === 'unavailable'
        ? 'unavailable'
        : doc.isError
          ? failureOf(doc.error)
          : null);
  const busy = scan.isPending || (!!pending && !failure);

  useEffect(() => {
    if (!busy) return;
    const timer = setTimeout(() => setSlow(true), STILL_WORKING_MS);
    return () => clearTimeout(timer);
  }, [busy]);

  // The check finished: act on what was read, once per document.
  const handled = useRef<string | null>(null);
  useEffect(() => {
    if (!pending || !d || !outcome || handled.current === d.id) return;
    handled.current = d.id;
    track('s0b_scan_result', { outcome });
    if (outcome === 'partial') go('S1');
    if (outcome !== 'full') return;
    // Full read: the enquiry now holds route, date, flight, name and booking reference.
    void qc
      .fetchQuery({
        queryKey: queryKeys.enquiry(pending.enquiryId),
        queryFn: ({ signal }) => api.getEnquiry(pending.enquiryId, { signal }),
        staleTime: 0,
      })
      .then((e: Enquiry) => goNext(e))
      .catch((e: unknown) => setUploadFailure(failureOf(e)));
  }, [d, outcome, pending, api, qc, go, goNext, track]);

  async function onFile(file: File) {
    track('s0b_scan_started', { type: file.type || 'unknown' });
    setUploadFailure(null);
    setSlow(false);
    setPending(null);
    handled.current = null;
    if (!file.size) {
      // A 0-byte file (a failed download, a cloud placeholder): say so, don't send it.
      setUploadFailure('empty');
      track('s0b_scan_result', { outcome: 'error', code: 'file_empty' });
      return;
    }
    // After "Check a different flight" the earlier check is in the draft, not the tab's current one.
    const previous = currentEnquiry.get() ?? tripDraft.get().prefillContactFrom ?? null;
    // A new check: nothing from an earlier trip may leak into what the scan reads.
    updateTripDraft({ origin: null, destination: null, connections: [], date: null });
    try {
      const attribution = tripDraft.get().attribution;
      const { enquiry, document } = await scan.mutateAsync({
        file,
        ...(previous ? { prefillContactFrom: previous } : {}),
        ...(Object.keys(attribution).length ? { attribution } : {}),
      });
      setPending({ enquiryId: enquiry.id, documentId: document.id });
    } catch (e) {
      setUploadFailure(failureOf(e));
      track('s0b_scan_result', { outcome: 'error', code: e instanceof ApiError ? e.code : null });
    }
  }

  function enterDetails() {
    track('s0b_enter_details');
    startNewCheck();
    go('S1');
  }

  return (
    <Screen
      id="S0b"
      title={t('S0b.title')}
      dock={<TextLink onClick={enterDetails}>{t('S0b.enterDetails')}</TextLink>}
    >
      <Question>{t('S0b.title')}</Question>
      <Sub>{t('S0b.sub')}</Sub>

      {busy ? (
        <>
          <ScanZone
            busy
            busyTitle={t('S0b.reading')}
            busySub={slow ? t('S0b.stillWorking') : undefined}
            title=""
            onFile={() => {}}
          />
          <TrustPills />
        </>
      ) : failure ? (
        <>
          <Hint warn>{t(`S0b.fail.${failure}`)}</Hint>
          <ScanRetry onFile={onFile} />
        </>
      ) : (
        <>
          <ScanZone title={t('S0b.zoneTitle')} sub={t('S0b.zoneSub')} onFile={onFile} />
          <Note>{t('S0b.privacy')}</Note>
        </>
      )}
    </Screen>
  );
}

/** "Try again": the same picker, as a ghost button under the failure notice. */
function ScanRetry({ onFile }: { onFile: (f: File) => void }) {
  const t = useT();
  return (
    <FileTrigger onFile={onFile}>
      {(open) => (
        <Button variant="ghost" onClick={open}>
          {t('S0b.retry')}
        </Button>
      )}
    </FileTrigger>
  );
}
